import flatten from 'lodash/flatten';
import { MutationType } from '@tencent/slide-sdk-pb-model';
import { shallowOmit } from '../utils/object-utils';
import { getPresentationKeyframe } from './get-presentation-keyframe';
import { getSectionKeyframe } from './get-section-keyframe';
import { getSlideKeyframe } from './get-page-keyframe';
import type { IValidMutationData } from '../mutation/base';
import type { IPresentationData } from '../data';
import type { KeyframeContext } from './keyframe-context.interface';

export interface ISlideFileKeyframe {
    presentation: IValidMutationData[];
    pages: {
        [id: string]: IValidMutationData[];
    };
}

export function getSlideFileKeyframe(presentation: IPresentationData): ISlideFileKeyframe {
    /**
     * 参考 library/collab/api/slide/fetch/page_data.cc 实现
     * slideMaster/slideLayout/notesMaster不分页，添加和属性一起序列化
     * slide/notes分页，因此presentation中只有添加
     */
    const { slideList, noteSlideMap, sectionMap } = presentation;
    const presentationWithoutSlideAndNote = {
        ...shallowOmit(presentation, 'slideList', 'noteSlideMap', 'sectionMap'),
        slideList: [],
        noteSlideMap: {},
        sectionMap: {},
    };
    const slideFileMut: {
        presentation: IValidMutationData[];
        pages: {
            [id: string]: IValidMutationData[];
        };
    } = {
        presentation: getPresentationKeyframe(presentationWithoutSlideAndNote),
        pages: {},
    };
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const context = {
        presentation: { noteSlideMap, slideList },
        idFactory: (id: string) => id,
    } as KeyframeContext;
    slideList.forEach((slide, index) => {
        slideFileMut.pages[slide.id] = [];
        slide.noteSlideId && (slideFileMut.pages[slide.noteSlideId] = []);
        const slideMut = getSlideKeyframe(slide, index, context);
        slideMut.forEach((mut) => {
            if (mut.type === MutationType.AddPage) {
                slideFileMut.presentation.push(mut);
            } else {
                // 注意，要保证后续新增的页内操作都有 pageId 字段
                slideFileMut.pages[(mut as { pageId: string }).pageId].push(mut);
            }
        });
    });
    // 这里最后手动添加 section 到 presentation，因为 section 依赖 slide
    slideFileMut.presentation.push(
        ...flatten(Object.values(sectionMap).map((section) => getSectionKeyframe(section, context))),
    );
    return slideFileMut;
}
